import { BIOME_IDS } from '../biomes.js';
import { SHEFIRA_AERIE_NAME, SHEFIRA_MOB_ID, SHEFIRA_ZONE_ID, } from '../shefira.js';
/**
 * Shefira's Aire (ADR 0068 §2): behind the crevasse of La Brèche, a short
 * climb through the rock, then her arena: round, twice a boss's hall so ten
 * Warriors can spread out, rimmed with steaming rock and open to the sky
 * (she rises and comes down). Built by hand from constants like the bosses'
 * dungeons (ADR 0027): the fight wants a room of a known size. Melted ice
 * and the Peaks' rock, no new terrain.
 */
export const AERIE_WIDTH = 45;
export const AERIE_HEIGHT = 58;
const FLOOR = 'n';
const WALL = 'r';
/** The arena: an ellipse round her home, a little wider than deep, like the dungeons' halls. */
export const AERIE_ARENA = { x: 22, y: 20, rx: 16, ry: 13 };
/** The climb up from the entrance to the arena, five tiles wide. */
const CLIMB = { x0: 20, x1: 24, y0: 33, y1: 43 };
/** Where one arrives from the crevasse. */
const ENTRY = { x0: 15, x1: 29, y0: 44, y1: 50 };
/** The way out: two tiles in the middle of the bottom, back to La Brèche. */
const DOOR = { x: 22, y: 53 };
/** Where Ulfar is thrown, out of the fight, alive (ADR 0068 §4): by the western rim. */
export const AERIE_THROWN = { x: 11, y: 22 };
/** The Aire's own props (`pixellab/props-aerie.json`): the Company's posts, the rim, the vents. */
const PROP = {
    ballista: 'station-ballista',
    'frost-brazier': 'station-frost-brazier',
    horn: 'station-horn',
    rock: 'aerie-rock',
    rockSmall: 'aerie-rock-small',
    vent: 'ember-vent',
};
/**
 * Nella's ballistae in two pairs across the arena, Ulfar's frost braziers on
 * the other diagonal, Maëlle's horn at the top where one sees the sky.
 */
export const AERIE_STATIONS = [
    { id: 'ballista-west', kind: 'ballista', x: 8, y: 20, opposite: 'ballista-east' },
    { id: 'ballista-east', kind: 'ballista', x: 36, y: 20, opposite: 'ballista-west' },
    { id: 'ballista-north-west', kind: 'ballista', x: 13, y: 12, opposite: 'ballista-south-east' },
    { id: 'ballista-south-east', kind: 'ballista', x: 31, y: 28, opposite: 'ballista-north-west' },
    { id: 'frost-brazier-south-west', kind: 'frost-brazier', x: 13, y: 28 },
    { id: 'frost-brazier-north-east', kind: 'frost-brazier', x: 31, y: 12 },
    { id: 'horn-north', kind: 'horn', x: 22, y: 9 },
];
/** Whether a tile lies in the arena: a little inside the true ellipse, no lone tile at its ends. */
export function inAerieArena(x, y) {
    const { x: cx, y: cy, rx, ry } = AERIE_ARENA;
    return ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 <= 0.92;
}
export function buildShefiraAerie() {
    const w = AERIE_WIDTH;
    const h = AERIE_HEIGHT;
    const grid = Array.from({ length: h }, () => new Array(w).fill(WALL));
    const open = (x, y) => {
        const row = grid[y];
        if (row !== undefined && x >= 2 && x < w - 2 && y >= 2 && y < h - 2)
            row[x] = FLOOR;
    };
    for (let y = 0; y < h; y += 1) {
        for (let x = 0; x < w; x += 1)
            if (inAerieArena(x, y))
                open(x, y);
    }
    for (const room of [CLIMB, ENTRY]) {
        for (let y = room.y0; y <= room.y1; y += 1) {
            for (let x = room.x0; x <= room.x1; x += 1)
                open(x, y);
        }
    }
    for (let y = ENTRY.y1 + 1; y <= DOOR.y; y += 1) {
        open(DOOR.x, y);
        open(DOOR.x + 1, y);
    }
    const home = { x: AERIE_ARENA.x, y: AERIE_ARENA.y };
    const props = [
        ...AERIE_STATIONS.map((station) => ({
            prop: PROP[station.kind],
            x: station.x,
            y: station.y,
            flip: station.x > home.x,
        })),
        // The rim steams: rocks fallen from the peaks round the edge.
        ...[
            [10, 15, 'rock'],
            [34, 15, 'rock'],
            [10, 25, 'rockSmall'],
            [34, 25, 'rockSmall'],
            [17, 31, 'rockSmall'],
            [27, 31, 'rock'],
            [17, 9, 'rockSmall'],
            [27, 9, 'rock'],
        ].map(([x, y, kind]) => ({ prop: PROP[kind], x, y, flip: x > home.x })),
        // Vents of embers in the floor, walked over: the ground is still hot.
        ...[
            [18, 13],
            [26, 13],
            [15, 21],
            [29, 21],
            [19, 27],
            [26, 28],
        ].map(([x, y]) => ({ prop: PROP.vent, x, y, flip: x > home.x })),
        // Braziers up the climb, three tiles of floor between them.
        ...[36, 40].flatMap((y) => [
            { prop: 'brazier', x: CLIMB.x0, y, flip: false },
            { prop: 'brazier', x: CLIMB.x1, y, flip: true },
        ]),
        // The daylight of the crevasse, the way back down to La Brèche.
        { prop: 'cave-daylight', x: DOOR.x, y: DOOR.y, flip: false },
    ];
    const ground = grid.map((row) => row.join(''));
    const rows = ground.map((row) => row.replace(/./g, (c) => (c === FLOOR ? '.' : '#')));
    const digit = String(BIOME_IDS.indexOf('snow'));
    return {
        id: SHEFIRA_ZONE_ID,
        name: SHEFIRA_AERIE_NAME,
        rows,
        ground,
        biomes: ground.map((row) => digit.repeat(row.length)),
        spawn: { x: DOOR.x, y: ENTRY.y1 - 1 },
        respawn: { x: DOOR.x, y: CLIMB.y0 + 2 },
        home,
        door: { x: DOOR.x, y: DOOR.y },
        arena: { centre: home, radius: AERIE_ARENA.rx },
        thrown: { ...AERIE_THROWN },
        props,
        dens: [{ mob: SHEFIRA_MOB_ID, ...home, count: 1, radius: 0 }],
        stations: AERIE_STATIONS,
    };
}
//# sourceMappingURL=shefira-aerie.js.map